import React from "react";
import {FormattedMessage} from "react-intl";
import {DayReport} from "@/data/entities/dayReport";
import {formatMoney} from "@/utils/MoneyUtils";
import {averageOrderValue, formatHoursMinutes, orderCount,} from "@widgets/reports/dayReport/DayReportUtils";
import {Badge, Stats} from "peggirkit";
import StatItem from "peggirkit/dist/components/Stats/StatsElements/StatItem";
import BadgeIconIndicator from "peggirkit/dist/components/Badge/BadgeElements/Indicator/BadgeIconIndicator";
import {XCircleIcon} from "@heroicons/react/16/solid";

type Props = {
    dayReport: DayReport,
};

const moneyOrDash = (amount: number | null): string => amount === null ? "–" : formatMoney(amount);

/**
 * Key figures of the day: revenue, orders, average order value and worked
 * hours.
 */
const DayReportStats = ({dayReport}: Props) => (
    <Stats
        size={"xs"}
        itemsPerRow={1}
        breakpoints={{
            sm: {
                itemsPerRow: 2,
            },
            xl: {
                size: "sm",
                itemsPerRow: 4,
            },
        }}
    >
        <StatItem
            name={<FormattedMessage id={"dayReport.orders"}/>}
            value={orderCount(dayReport)}
            indication={
                <Badge
                    indicator={<BadgeIconIndicator icon={XCircleIcon}/>}
                    color={"danger"}
                    size={"xs"}
                >
                    <FormattedMessage
                        id={"dayReport.orders.description"}
                        values={{n: dayReport.cancelledOrderCount}}
                    />
                </Badge>
            }
        />
        <StatItem
            name={<FormattedMessage id={"dayReport.grossRevenue"}/>}
            value={formatMoney(dayReport.grossRevenue)}
            indication={
                <Badge
                    color={"secondary"}
                    size={"xs"}
                >
                    <FormattedMessage
                        id={"dayReport.grossRevenue.description"}
                        values={{net: formatMoney(dayReport.netRevenue)}}
                    />
                </Badge>
            }
        />
        <StatItem
            name={<FormattedMessage id={"dayReport.averageOrderValue"}/>}
            value={moneyOrDash(averageOrderValue(dayReport))}
        />
        <StatItem
            name={<FormattedMessage id={"dayReport.workedTime"}/>}
            value={formatHoursMinutes(dayReport.workedMinutes)}
        />
    </Stats>
);

export default DayReportStats;